import {SectionBgView} from '../../ui_canvas_engine/SectionBgView'
import {CA} from '../../analytics/Analytics'
import {TextButtonView} from '../../ui_canvas_engine/TextButtonView'
import {UIGameManager} from '../UIGameManager'
import {UIGameMechanic} from '../UIGameEventController'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import UIGameEventEnums = GameStateEnums.UIGameEventEnums
import {EndGameButtonTypes, getEndGameRightButtonType} from '@colonist/game-endgame-types'
import {GameTypeEnum, MatchmakingMatchTypes, ModeSettingEnum} from '@colonist/game-base-types'
import {UIEntryUrlParameterController} from '../../UIEntryUrlParameterController'
import {ViewPopupMembershipIncentiveEndgame} from '../../ui_popup/ui_popup_membership_incentive/ViewPopupMembershipIncentiveEndgame'
import {UIUrlParameterNames} from '../../ui_html/UIUrlController'
import {OpacityAnimationWrapper} from '../ui_game_animations/OpacityAnimationWrapper'
import {socket} from '../../../../shared/scripts/EntryShared'
import {TranslationObject} from '@colonist/i18n'
import {UserSessionUtils} from '../../UserSessionUtils'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {UIFeedbackFormPopupController} from '../../ui_popup/ui_popup_feedback_form/UIFeedbackFormPopupController'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {AnalyticEventType, FeedbackFormLocationEnum} from '../../../../../shared/entity_datas/EntityDatas'
import {FeedbackFormUtils} from '../../ui_popup/ui_popup_feedback_form/FeedbackFormUtils'
import {ViewPopupChangeOnlineUsername} from '../../ui_popup/ViewPopupChangeOnlineUsername'
import {StorageWrapper} from '../../StorageWrapper'
import {GameFont} from '../../GameFont'
import {getHomepageUrl, getHomepageUrlWithParameter, navigateToPage} from '../../ui_utils/ClientUtils'
import {PlayerColor} from '@colonist/game-states'
import {ViewPopupKeepPlayingOnWeb} from '../../ui_popup/ViewPopupKeepPlayingOnWeb'
import {DurationConverter, TimeUtils} from '@colonist/utils'

export class UIGameEndFooterController implements UIGameMechanic {
    private readonly uiGameManager: UIGameManager

    readonly container: SectionBgView

    private readonly REMATCH_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.rematch'}
    private readonly CONTINUE_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.continue'}
    private readonly HOME_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.home'}
    private readonly PLAY_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.play'}
    private readonly NEXT_LEVEL_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.nextLevel'}
    private readonly RETRY_TEXT: TranslationObject = {key: 'strings:game.endGame.footer.retry'}

    private readonly buttonY: number
    private readonly buttonW: number
    private readonly buttonH: number

    private readonly generalButtons: TextButtonView[]

    private readonly winnerColor: PlayerColor
    private readonly isShownAfterLiveGame: boolean

    private lobbyButton: TextButtonView | undefined
    private rematchButton: TextButtonView | undefined
    private continueButton: TextButtonView | undefined

    private isFirstClickOnBackToLobby: boolean

    constructor(uiGameManager: UIGameManager, y: number, h: number, container: SectionBgView, winnerColor: PlayerColor, isShownAfterLiveGame: boolean) {
        this.uiGameManager = uiGameManager
        this.winnerColor = winnerColor
        this.isShownAfterLiveGame = isShownAfterLiveGame
        this.uiGameManager.eventController.registerUIGameMechanic(this)
        this.container = new SectionBgView(0, y, container.width, h, container)
        this.buttonY = 0

        this.isFirstClickOnBackToLobby = true

        const isUserOnMobileApp = (UIUtilsEjsVariables.isUserOnMobileApp && UIUtilsEjsVariables.shouldDisplayNewMobileGameUI)
        this.buttonW = this.container.width * (isUserOnMobileApp ? 0.4 : 0.25)
        this.buttonH = this.container.height * (isUserOnMobileApp ? 0.8 : 0.56)

        this.generalButtons = []
        this.createGeneralButtons()
        this.showGeneralButtons()
        this.centerGeneralButtonIfOnlyOne()
    }

    onEvent(data: any, eventType: UIGameEventEnums) {
        switch(eventType) {
            case UIGameEventEnums.GameEndRematchTextUpdated: this.updatePlayerCount(data); break
            default: break
        }
    }

    setVisible(visible: boolean) {
        this.container.visible = visible
    }

    private createGeneralButtons() {
        const leftButtonX = this.container.width / 2 - this.buttonW * 1.1
        const rightButtonX = this.container.width / 2 + this.buttonW * 0.1

        for(const endGameButton of this.getEndGameButtons()) {
            switch(endGameButton) {
                case EndGameButtonTypes.Rematch: this.createRematchButton(rightButtonX); break
                case EndGameButtonTypes.Lobby: this.createLobbyButton(leftButtonX); break
                case EndGameButtonTypes.Retry: this.createRetryButton(rightButtonX); break
                case EndGameButtonTypes.MatchmakingPlayAgain: this.createPlayAgainButton(rightButtonX); break
                case EndGameButtonTypes.NextLevel: this.createNextLevelButton(rightButtonX); break
                default: break
            }
        }
    }

    private getEndGameButtons(): EndGameButtonTypes[] {
        if(!this.isShownAfterLiveGame) return [EndGameButtonTypes.Lobby]

        const isCampaign = this.uiGameManager.gameController.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        const isMatchmakingGame = this.uiGameManager.gameController.gameSettings.gameType == GameTypeEnum.MatchmakingGame || this.uiGameManager.gameController.gameSettings.gameType == GameTypeEnum.MatchmakingGameRanked
        const isGameWonByHuman = this.uiGameManager.gameController.getPlayerState(this.winnerColor).isUser

        const rightButton = getEndGameRightButtonType(isCampaign, isMatchmakingGame, isGameWonByHuman)
        const leftButton = EndGameButtonTypes.Lobby

        return this.shouldHideRematchButton() ? [leftButton] : [leftButton, rightButton]
    }

    // 11 Feb 2024. Oguz: Temporary solution for LobbyAction.Create1v1BotGame on Discord. Read #15023
    // This should be removed once the Discord flow is updated to handle the game creation from game end screen.
    private shouldHideRematchButton(): boolean {
        if(this.uiGameManager.gameController.isPlayerSpectator) return true
        if(!this.uiGameManager.gameController.gameDetails.isDiscord) return false
        if(this.uiGameManager.gameController.gameSettings.gameType != GameTypeEnum.BotGame) return false
        if(this.uiGameManager.gameController.botSessionCount() != 1) return false
        return true
    }

    showGeneralButtons() {
        if(this.continueButton != undefined) this.continueButton.hide()
        for(const generalButton of this.generalButtons) {
            this.showButton(generalButton)
        }
    }

    showContinueButton(action: () => void) {
        for(const generalButton of this.generalButtons) {
            generalButton.hide()
        }

        this.createContinueButton(action)
    }

    private showButton(button: TextButtonView) {
        this.container.addChild(button)
    }

    private updatePlayerCount(playersJoined: number) {
        if(this.rematchButton == undefined) return
        const playersJoinedText: TranslationObject = {
            key: 'strings:game.endGame.footer.playersJoined',
            options: {
                playerCount: playersJoined,
                maxPlayerCount: this.uiGameManager.gameController.gameSettings.maxPlayers,
            },
        }

        if(UIUtilsEjsVariables.isUserOnMobileApp && !UIUtilsEjsVariables.isUserOnTabletApp) {
            this.rematchButton.getTextView().changeStyle({fontSize: Math.min(this.buttonH / 4, GameFont.Size.XL)})
        }
        this.rematchButton.updateText(playersJoinedText)
    }

    private createRematchButton(buttonX: number) {
        this.rematchButton = new TextButtonView(buttonX, this.buttonY, this.buttonW, this.buttonH, this.container)
        this.rematchButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_green_cta))
        this.rematchButton.updateText(this.REMATCH_TEXT)

        let isFirstClick = true
        this.rematchButton.updateAction(() => {
            void this.showPopupsBeforeExitingGame(() => {
                CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
                const isSocketOpen = socket?.controller.isSocketOpen()
                if(isSocketOpen) {
                    this.uiGameManager.socketGameSend.clickedRematch()
                    return
                }
                window.location.hash = socket.gameListener.databaseGameId
                window.location.reload()
            }, isFirstClick, this.REMATCH_TEXT)
            isFirstClick = false
        })
        this.generalButtons.push(this.rematchButton)
    }

    private createLobbyButton(buttonX: number) {
        this.lobbyButton = new TextButtonView(buttonX, this.buttonY, this.buttonW, this.buttonH, this.container)
        this.lobbyButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_hollow_cta))
        this.lobbyButton.updateText(this.HOME_TEXT)
        this.lobbyButton.updateAction(() => {
            void this.handleExitingGame()
        })
        this.generalButtons.push(this.lobbyButton)
    }

    private createPlayAgainButton(buttonX: number) {
        const playAgainButton = new TextButtonView(buttonX, this.buttonY, this.buttonW, this.buttonH, this.container)
        playAgainButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_green_cta))
        playAgainButton.updateText(this.PLAY_TEXT)
        let isFirstClick = true
        playAgainButton.updateAction(() => {
            void this.showPopupsBeforeExitingGame(() => {
                this.uiGameManager.gameController.gameDetails.matchmakingMatchType == undefined ? UIGameEndFooterController.sendToFindGameSelector() : UIGameEndFooterController.sendDirectlyToMatchmakingQueue(this.uiGameManager.gameController.gameDetails.matchmakingMatchType)
            }, isFirstClick, this.PLAY_TEXT)
            isFirstClick = false
        })
        this.generalButtons.push(playAgainButton)
    }

    private createNextLevelButton(buttonX: number) {
        const nextLevelButton = new TextButtonView(buttonX, this.buttonY, this.buttonW, this.buttonH, this.container)
        nextLevelButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_green_cta))
        nextLevelButton.updateText(this.NEXT_LEVEL_TEXT)
        nextLevelButton.updateAction(async() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameRematch)
            await this.uiGameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedNextLevel, StorageWrapper.getBeginnerModeLevel())
            UIGameEndFooterController.sendToNextCampaignLevel()
        })
        this.generalButtons.push(nextLevelButton)
    }

    private createRetryButton(buttonX: number) {
        const retryButton = new TextButtonView(buttonX, this.buttonY, this.buttonW, this.buttonH, this.container)
        retryButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_green_cta))
        retryButton.updateText(this.RETRY_TEXT)
        retryButton.updateAction(async() => {
            StorageWrapper.decrementBeginnerModeLevel()
            await this.uiGameManager.socketGameSend.sendGameAnalyticOnce(AnalyticEventType.BeginnerModeClickedRetry, StorageWrapper.getBeginnerModeLevel())
            UIGameEndFooterController.sendToNextCampaignLevel()
        })
        this.generalButtons.push(retryButton)
    }

    private static sendToLobby() {
        const redirectUrl = UIGameEndFooterController.shouldShowShareActivityLinkPopup() ? getHomepageUrlWithParameter(UIUrlParameterNames.DiscordShareActivity, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE) : getHomepageUrl()

        window.history.pushState('', document.title, redirectUrl)
        location.reload()
    }

    private static sendDirectlyToMatchmakingQueue(matchType: MatchmakingMatchTypes) {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.EnterMatchmaking, matchType))
    }

    private static sendToFindGameSelector() {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.FindGameSelector, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    private static sendToNextCampaignLevel() {
        navigateToPage(getHomepageUrlWithParameter(UIUrlParameterNames.Campaign, UIEntryUrlParameterController.DEFAULT_PARAMETER_VALUE))
    }

    handleExitingGame() {
        void this.showPopupsBeforeExitingGame(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameLobby)
            UIGameEndFooterController.sendToLobby()
        }, this.isFirstClickOnBackToLobby, this.HOME_TEXT)
        this.isFirstClickOnBackToLobby = false
    }

    private createContinueButton(action: () => void) {
        this.continueButton = new TextButtonView(0, this.buttonY, this.buttonW, this.buttonH, this.container)
        this.continueButton.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_green_cta))
        this.continueButton.updateText(this.CONTINUE_TEXT)
        this.continueButton.centerXInParent()

        this.continueButton.updateAction(action)
    }

    private centerGeneralButtonIfOnlyOne() {
        if(this.generalButtons.length != 1) return
        this.generalButtons[0].centerXInParent()
    }

    fadeInButtons(startingDelayInMilliseconds: number) {
        new OpacityAnimationWrapper(this.container, this.uiGameManager, 0, 1, 300, startingDelayInMilliseconds)
    }

    private async showPopupsBeforeExitingGame(continuationAction: () => void, isFirstClick: boolean, continuationText: TranslationObject = this.CONTINUE_TEXT) {
        if(!isFirstClick) return continuationAction()

        const shouldShowDiscordKeepPlayingOnWebPopup = UserSessionUtils.canShowDiscordKeepPlayingOnWebPopup()
        if(shouldShowDiscordKeepPlayingOnWebPopup) {
            new ViewPopupKeepPlayingOnWeb(continuationAction)
            StorageWrapper.setDiscordKeepPlayingOnWebPopupShown()
            return
        }

        const shouldShowChangeUsernamePopup = UserSessionUtils.shouldShowChangeUsernamePopup(this.uiGameManager.gameController.gameDetails.matchmakingMatchType)
        if(shouldShowChangeUsernamePopup) {
            new ViewPopupChangeOnlineUsername(continuationAction)
            StorageWrapper.setDiscordOnlineUsernamePopupShown()
            return
        }

        const shouldShowFeedbackForm = FeedbackFormUtils.shouldShowFeedbackForm()
        if(shouldShowFeedbackForm) {
            new UIFeedbackFormPopupController(() => continuationAction(), socket.gameListener.databaseGameId, FeedbackFormLocationEnum.EndGame)
            return
        }

        const shouldShowMembershipIncentive = UserSessionUtils.canShowSubscriptionPopup()
        if(shouldShowMembershipIncentive) {
            new ViewPopupMembershipIncentiveEndgame(continuationAction, this.uiGameManager.gameController.gameSettings, continuationText)
            return
        }

        continuationAction()
    }

    private static shouldShowShareActivityLinkPopup(): boolean {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return false

        const lastPopupTime = StorageWrapper.getLastShareActivityLinkPopupTime()
        if(lastPopupTime == null) return true

        const millisecondsSinceLastPopupTime = TimeUtils.millisecondsPassedSince(lastPopupTime.getTime())
        const shownWithinLastDay = millisecondsSinceLastPopupTime < DurationConverter.daysAsMilliseconds(1)

        return !shownWithinLastDay
    }
}
